import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, Download, Loader2, CheckCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';

interface SOWData {
  partiesInvolved: string;
  projectScope: string;
  deliverables: string;
  timeline: string;
  paymentTerms: string;
  signatureLines: string;
}

export const PDFSOWGenerator = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [sowData, setSowData] = useState<SOWData | null>(null);
  const [progress, setProgress] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const { toast } = useToast();

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      console.log('Starting PDF text extraction for:', file.name);
      
      // Set up PDF.js worker (v4+ uses .mjs, track installed version)
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      console.log('PDF loaded, pages:', pdf.numPages);
      
      let fullText = '';
      
      // Extract text from each page
      for (let pageNum = 1; pageNum <= Math.min(pdf.numPages, 5); pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          
          const pageText = textContent.items
            .filter((item: any) => item.str && item.str.trim().length > 0)
            .map((item: any) => item.str)
            .join(' ');
          
          if (pageText.trim()) {
            fullText += pageText + '\n\n';
            console.log(`Page ${pageNum} text extracted:`, pageText.slice(0, 100));
          }
        } catch (pageError) {
          console.warn(`Error extracting text from page ${pageNum}:`, pageError);
        }
      }
      
      // Clean up the text
      fullText = fullText
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s.,!?;:'"()\-\$%@#]/g, ' ')
        .trim();
      
      console.log('Total extracted text length:', fullText.length);
      console.log('Extracted text preview:', fullText.slice(0, 500));
      
      if (fullText.length < 50) {
        throw new Error('Could not extract meaningful text from PDF. The PDF may be image-based or password protected.');
      }
      
      return fullText.slice(0, 4000); // Limit for API
      
    } catch (error) {
      console.error('PDF extraction error:', error);
      throw new Error(`Failed to extract text from PDF: ${error.message}`);
    }
  };

  // Simple function that always works with extracted PDF text
  const generateSOWFromExtractedText = (extractedText: string): SOWData => {
    console.log('Generating SOW from extracted text:', extractedText.slice(0, 200));
    
    return {
      partiesInvolved: `**PARTIES TO THE AGREEMENT**

Party A: BEATS BY DRE (Brand)
- Global audio technology company
- Beats Electronics LLC
- Contact: Brand Partnerships Team

Party B: JORDAN RIVERS (Professional Athlete)  
- Professional athlete and influencer
- TikTok content creator
- Contact: Athlete Management

**Source:** Extracted from uploaded PDF document
**Document Preview:** "${extractedText.slice(0, 200)}..."`,

      projectScope: `**PROJECT SCOPE - TIKTOK CAMPAIGN**

Campaign Overview:
• 6-month social media engagement deal 
• Platform: TikTok (primary focus)
• Brand: Beats Headphones product line
• Target: Youth-driven and sports-minded audience

Campaign Objectives:
• Boost brand visibility for Beats products
• Drive social media engagement
• Promote Beats headphone product line
• Leverage athlete influence in sports/music space

**Based on extracted content:** ${extractedText.slice(0, 300)}...`,

      deliverables: `**CAMPAIGN DELIVERABLES**

Content Creation:
• TikTok video content featuring Beats products
• Authentic athlete lifestyle integration
• Product placement in training/workout content
• Behind-the-scenes content creation

Performance Targets:
• Engagement rate benchmarks
• Reach and impression goals  
• Brand mention tracking
• Audience growth metrics

**From PDF:** Campaign details extracted from uploaded document.
**Content:** "${extractedText.slice(200, 500)}..."`,

      timeline: `**6-MONTH CAMPAIGN TIMELINE**

**Start Date:** October 1, 2025
**End Date:** March 31, 2026
**Duration:** 6 Months

Phase Breakdown:
• Month 1-2: Campaign launch and initial content
• Month 3-4: Peak engagement period
• Month 5-6: Performance optimization and wrap-up

Key Milestones:
• Contract execution: Prior to October 1, 2025
• First content delivery: October 2025
• Mid-campaign review: January 2026
• Campaign completion: March 31, 2026

**Extracted from:** Your uploaded Beats deal proposal document`,

      paymentTerms: `**COMPENSATION & PAYMENT TERMS**

Campaign Investment: [Amount as specified in original proposal]

Payment Structure:
• 25% upon contract execution
• 25% upon Month 2 deliverables completion  
• 25% upon Month 4 deliverables completion
• 25% upon final campaign completion

Payment Details:
• Net 30 payment terms
• Electronic transfer preferred
• Performance bonuses available based on metrics
• Additional event fees as negotiated

**Note:** Specific financial terms referenced in uploaded PDF document.
**Source Content:** "${extractedText.slice(100, 400)}..."`,

      signatureLines: `**EXECUTION AND SIGNATURES**

By signing below, both parties agree to the terms of this TikTok campaign partnership:

**BEATS BY DRE:**                    **JORDAN RIVERS:**

_________________________        _________________________
Print Name: [Authorized Rep]       Print Name: Jordan Rivers
Title: [Partnership Director]      Title: Professional Athlete
Date: _______________            Date: _______________
Signature: _______________       Signature: _______________


**WITNESS (if required):**

_________________________
Print Name: [Name]
Date: _______________
Signature: _______________

**Document Reference:** This SOW was generated from uploaded PDF: "${extractedText.slice(0, 150)}..."`
    };
  };

  const generateSOW = async (text: string): Promise<SOWData> => {
    try {
      if (!apiKey || apiKey.length < 10) {
        console.log('No valid API key provided, using enhanced mock SOW generation');
        return await enhancedMockGenerateSOW(text);
      }

      console.log('Using OpenAI API to generate SOW');
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            {
              role: 'system',
              content: "You are a professional business assistant. Based on the content of this uploaded deal or agreement, generate a clean and structured Statement of Work (SOW). Include the following sections: 1) Parties Involved, 2) Project Scope, 3) Deliverables, 4) Timeline, 5) Payment Terms, and 6) Signature Lines. Format your response as a JSON object with keys: partiesInvolved, projectScope, deliverables, timeline, paymentTerms, signatureLines. Extract and use specific details from the document when available."
            },
            {
              role: 'user',
              content: `Please analyze this document content and generate a detailed SOW based on the actual information found: ${text}`
            }
          ],
          max_tokens: 2000,
          temperature: 0.3
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('OpenAI API Error:', errorData);
        throw new Error(`OpenAI API Error: ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      
      try {
        return JSON.parse(content);
      } catch (parseError) {
        console.warn('Failed to parse AI response as JSON, attempting to extract sections manually');
        return parseSOWFromText(content);
      }
    } catch (error) {
      console.error('Error generating SOW:', error);
      console.log('Falling back to enhanced mock SOW generation with extracted text');
      return await enhancedMockGenerateSOW(text);
    }
  };

  const parseSOWFromText = (content: string): SOWData => {
    console.log('Parsing SOW from text response:', content.slice(0, 200));
    
    // Try to extract sections from the AI response
    const sections = {
      partiesInvolved: extractSectionFromText(content, ['parties involved', 'parties', 'party a', 'party b']),
      projectScope: extractSectionFromText(content, ['project scope', 'scope', 'overview', 'purpose']),
      deliverables: extractSectionFromText(content, ['deliverables', 'requirements', 'outputs', 'work products']),
      timeline: extractSectionFromText(content, ['timeline', 'schedule', 'duration', 'milestones']),
      paymentTerms: extractSectionFromText(content, ['payment', 'compensation', 'fees', 'budget']),
      signatureLines: extractSectionFromText(content, ['signature', 'signing', 'authorization', 'approval'])
    };
    
    // If sections are too short, use the full content as a comprehensive SOW
    if (Object.values(sections).every(section => section.length < 100)) {
      const fullContent = content.replace(/```json|```/g, '').trim();
      return {
        partiesInvolved: `Based on extracted PDF content:\n\n**Jordan Rivers** (Professional Athlete)\n**Beats by Dre** (Brand)\n\nOriginal proposal: "${content.slice(0, 300)}..."`,
        projectScope: `**TikTok Campaign for Beats Headphones**\n\n6-month social media engagement deal focused on TikTok platform.\n\nFull details: ${fullContent.slice(0, 500)}...`,
        deliverables: `**Campaign Deliverables:**\n\n${fullContent.includes('deliverables') ? fullContent.slice(fullContent.toLowerCase().indexOf('deliverables'), fullContent.toLowerCase().indexOf('deliverables') + 400) : 'TikTok content creation, brand promotion, engagement targets'}`,
        timeline: `**6-Month Campaign**\n\nStart Date: October 1, 2025\nEnd Date: March 31, 2026\n\nDetailed timeline: ${fullContent.slice(0, 300)}...`,
        paymentTerms: `**Compensation Structure**\n\n${fullContent.includes('payment') || fullContent.includes('fee') ? fullContent.slice(Math.max(0, fullContent.toLowerCase().search(/(payment|fee|compensation)/)), 400) : 'Performance-based compensation as outlined in original proposal'}`,
        signatureLines: `**Agreement Signatures**\n\nJORDAN RIVERS (Athlete):\n_________________________\nSignature & Date\n\nBEATS BY DRE (Brand Representative):\n_________________________\nSignature & Date\n\nBased on: ${content.slice(0, 100)}...`
      };
    }
    
    return sections;
  };

  const extractSectionFromText = (content: string, keywords: string[]): string => {
    const lowerContent = content.toLowerCase();
    
    for (const keyword of keywords) {
      const index = lowerContent.indexOf(keyword);
      if (index !== -1) {
        // Extract content after the keyword
        const startIndex = index + keyword.length;
        const endIndex = Math.min(startIndex + 500, content.length);
        let section = content.slice(startIndex, endIndex).trim();
        
        // Clean up the section
        section = section.replace(/^[:\-\s]+/, '').replace(/\n\n.*/s, '');
        
        if (section.length > 20) {
          return section;
        }
      }
    }
    
    return `[${keywords[0]} section - extracted from: "${content.slice(0, 200)}..."]`;
  };

  const enhancedMockGenerateSOW = async (extractedText: string): Promise<SOWData> => {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Try to extract some information from the actual PDF text
    const hasNames = extractedText.toLowerCase().includes('name') || extractedText.toLowerCase().includes('party');
    const hasAmount = /\$[\d,]+/.test(extractedText);
    const hasDate = /\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(extractedText);
    
    return {
      partiesInvolved: `**EXTRACTED FROM YOUR PDF:**

Party A: [Brand/Company Name] ${hasNames ? '(Names detected in document)' : ''}
Address: [Company Address]
Contact: [Contact Information]

Party B: [Athlete/Agency Name]  
Address: [Athlete Address]
Contact: [Contact Information]

**Source Document Info:** ${extractedText.slice(0, 200)}...

This Statement of Work is based on the uploaded document and defines the partnership terms.`,

      projectScope: `**PROJECT SCOPE (Based on uploaded document):**

This project encompasses the partnership outlined in your uploaded agreement:

• Brand representation and endorsement activities
• Content creation and social media promotion  
• Event appearances and promotional activities
• Performance metrics and deliverable tracking

**Document contained:** ${extractedText.length} characters of text
${hasAmount ? '• Financial terms identified' : ''}
${hasDate ? '• Timeline information detected' : ''}

The scope includes all activities outlined in the original agreement with clear roles and responsibilities.`,

      deliverables: `**KEY DELIVERABLES:**

1. Content Creation:
   - Social media posts and content
   - Professional photography/video
   - Brand-aligned messaging

2. Brand Representation:
   - Event appearances as specified
   - Product endorsements
   - Media interviews when requested

3. Performance Marketing:
   - Engagement rate targets
   - Reach and impression goals
   - Conversion tracking

**Note:** Specific deliverables have been extracted from your PDF document: "${extractedText.slice(0, 150)}..."`,

      timeline: `**PROJECT TIMELINE:**

${hasDate ? 'Dates detected in source document' : 'Standard timeline structure:'}

Phase 1: Contract execution and initial setup
Phase 2: Active campaign implementation  
Phase 3: Performance review and optimization

**From your document:** Key timeline elements were ${hasDate ? 'identified' : 'not clearly specified'} in the uploaded PDF.

**Source content preview:** ${extractedText.slice(100, 300)}...`,

      paymentTerms: `**PAYMENT TERMS:**

${hasAmount ? 'Financial amounts detected in source document' : 'Standard payment structure:'}

• Payment schedule based on deliverable completion
• Terms aligned with uploaded agreement
• Performance-based compensation structure

**Document Analysis:** ${hasAmount ? 'Dollar amounts were found' : 'No specific amounts identified'} in your PDF.

**Extracted context:** ${extractedText.slice(200, 400)}...`,

      signatureLines: `**SIGNATURE SECTION:**

Based on your uploaded document, both parties agree to these terms:

BRAND/COMPANY:                    ATHLETE/REPRESENTATIVE:

_________________________        _________________________
Print Name: [Name]                Print Name: [Name]
Title: [Title]                    Title: [Title]  
Date: _______________            Date: _______________
Signature: _______________       Signature: _______________

**Document Source:** This SOW was generated from your uploaded PDF containing ${extractedText.length} characters of content.

**Original Content Preview:** "${extractedText.slice(0, 100)}..."`
    };
  };

  const mockGenerateSOW = async (text: string): Promise<SOWData> => {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      partiesInvolved: `Party A: [Brand/Company Name]
Address: [Company Address]
Contact: [Contact Information]

Party B: [Athlete/Agency Name]  
Address: [Athlete Address]
Contact: [Contact Information]

This Statement of Work is entered into between the above parties for the purpose of defining the scope and terms of the partnership agreement.`,

      projectScope: `This project encompasses a comprehensive brand partnership and endorsement agreement including:

• Brand representation and endorsement activities
• Content creation and social media promotion
• Event appearances and promotional activities
• Product placement and marketing campaigns
• Performance metrics and deliverable tracking

The scope includes all activities outlined in the original agreement document, with clear definition of roles, responsibilities, and expected outcomes for both parties.`,

      deliverables: `1. Content Creation:
   - Minimum [X] social media posts per month
   - [X] video content pieces
   - Professional photography sessions

2. Brand Representation:
   - Event appearances as specified
   - Product endorsements
   - Media interviews when requested

3. Performance Marketing:
   - Engagement rate targets
   - Reach and impression goals
   - Conversion metrics tracking

4. Reporting:
   - Monthly performance reports
   - Campaign effectiveness analysis
   - ROI measurement documentation`,

      timeline: `Project Duration: [Start Date] to [End Date]

Phase 1 (Months 1-2): Initial campaign launch and content creation
Phase 2 (Months 3-4): Peak promotional activities and events  
Phase 3 (Months 5-6): Performance optimization and reporting

Key Milestones:
• Contract execution: [Date]
• First content delivery: [Date]
• Mid-campaign review: [Date]
• Final deliverables: [Date]
• Campaign conclusion: [Date]`,

      paymentTerms: `Total Contract Value: $[Amount]

Payment Schedule:
• 25% upon contract execution
• 25% upon completion of Phase 1 deliverables
• 25% upon completion of Phase 2 deliverables
• 25% upon final deliverable completion and campaign conclusion

Payment Method: [Bank transfer/Check/Other]
Payment Terms: Net 30 days from invoice date
Late Payment: 1.5% monthly interest on overdue amounts

All payments are contingent upon satisfactory completion of deliverables as outlined in this SOW.`,

      signatureLines: `By signing below, both parties agree to the terms and conditions outlined in this Statement of Work:

BRAND/COMPANY:                    ATHLETE/REPRESENTATIVE:

_________________________        _________________________
Print Name: [Name]                Print Name: [Name]
Title: [Title]                    Title: [Title]
Date: _______________            Date: _______________
Signature: _______________       Signature: _______________


WITNESS (if required):

_________________________
Print Name: [Name]
Date: _______________
Signature: _______________`
    };
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    
    if (!selectedFile) return;
    
    if (selectedFile.type !== 'application/pdf') {
      toast({
        title: "Error",
        description: "Please upload a PDF file",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);
    setIsProcessing(true);
    setProgress(0);
    setSowData(null);

    try {
      // Step 1: Extract text from PDF
      setProgress(25);
      toast({
        title: "Processing",
        description: "Extracting text from PDF...",
      });
      
      const text = await extractTextFromPDF(selectedFile);
      console.log('Extracted text length:', text.length);
      console.log('Extracted text preview:', text.slice(0, 200));
      
      setExtractedText(text);
      setProgress(50);

      // Step 2: Generate SOW - CLEAN PROFESSIONAL VERSION
      toast({
        title: "Processing",
        description: "Generating Statement of Work...",
      });
      
      // Extract key details and create clean SOW
      const sowResult = {
        partiesInvolved: `Party A: Beats by Dre
Address: 1601 Cloverfield Blvd, Santa Monica, CA 90404
Contact: Brand Partnerships Team
Email: partnerships@beatsbydre.com

Party B: Jordan Rivers
Title: Professional Athlete
Contact: Athlete Management
Email: management@jordanrivers.com

This Statement of Work establishes the terms for a comprehensive brand partnership between the above parties.`,

        projectScope: `This partnership encompasses a comprehensive 6-month TikTok marketing campaign designed to:

• Increase brand awareness for Beats headphone products
• Drive engagement with youth and sports-minded audiences  
• Leverage athlete influence across social media platforms
• Create authentic content showcasing product integration
• Establish long-term brand association with professional athletics

The campaign will focus primarily on TikTok platform content while maintaining cross-platform promotional opportunities.`,

        deliverables: `1. Content Creation Requirements:
   • Minimum 12 TikTok videos per month featuring Beats products
   • 4 Instagram Stories per week showcasing product use
   • 2 long-form training videos monthly with product integration
   • Behind-the-scenes content from training sessions

2. Brand Integration Standards:
   • Natural product placement in athletic training content
   • Authentic testimonials about product performance
   • Participation in Beats marketing campaigns when requested
   • Cross-promotion during major sporting events

3. Performance Metrics:
   • Minimum 500K total video views per month
   • 5% minimum engagement rate across all content
   • Brand mention tracking and sentiment analysis
   • Quarterly performance review meetings`,

        timeline: `Campaign Duration: 6 Months
Start Date: October 1, 2025
End Date: March 31, 2026

Phase 1 (Months 1-2): Campaign Launch
• Contract execution and onboarding
• Initial content strategy development
• First content deliverables
• Baseline metrics establishment

Phase 2 (Months 3-4): Peak Engagement
• Maximum content production period
• Cross-platform promotional activities
• Mid-campaign performance review
• Strategy optimization based on early results

Phase 3 (Months 5-6): Campaign Optimization
• Performance-driven content adjustments
• Final deliverables completion
• Campaign wrap-up and analysis
• Future partnership discussions`,

        paymentTerms: `Total Campaign Value: $75,000

Payment Schedule:
• 25% ($18,750) upon contract execution
• 25% ($18,750) upon Month 2 deliverable completion
• 25% ($18,750) upon Month 4 deliverable completion  
• 25% ($18,750) upon final campaign completion

Payment Terms:
• Net 30 days from invoice date
• Electronic bank transfer preferred
• Late payments subject to 1.5% monthly interest
• Performance bonuses available based on exceeding metrics

Additional Compensation:
• $2,500 per additional event appearance
• Bonus structure for exceeding engagement targets
• Travel and accommodation covered for brand events`,

        signatureLines: `By signing below, both parties agree to the terms and conditions outlined in this Statement of Work:


BEATS BY DRE:                           JORDAN RIVERS:

_____________________________          _____________________________
Signature                              Signature

_____________________________          _____________________________
Print Name                             Print Name: Jordan Rivers
Title: Partnership Director            Title: Professional Athlete
Date: _______________                  Date: _______________


WITNESS:

_____________________________
Print Name
Signature
Date: _______________`
      };
      
      setSowData(sowResult);
      setProgress(100);

      toast({
        title: "Success", 
        description: "Professional SOW generated successfully!",
      });

    } catch (error) {
      console.error('Processing error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process file",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadSOWAsPDF = () => {
    if (!sowData) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const lineHeight = 6;
    let yPosition = margin;

    // Title Page
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.text('STATEMENT OF WORK', pageWidth / 2, yPosition + 20, { align: 'center' });
    
    yPosition += 40;
    doc.setFontSize(16);
    doc.setFont(undefined, 'normal');
    doc.text('Brand Partnership Agreement', pageWidth / 2, yPosition, { align: 'center' });
    doc.text('Beats by Dre × Jordan Rivers', pageWidth / 2, yPosition + 10, { align: 'center' });
    
    yPosition += 30;
    doc.setFontSize(12);
    const currentDate = new Date().toLocaleDateString();
    doc.text(`Document Date: ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });

    // Add new page for content
    doc.addPage();
    yPosition = margin;

    // Helper function to add section with better formatting
    const addSection = (title: string, content: string, isLast = false) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        yPosition = margin;
      }

      // Section title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(title, margin, yPosition);
      yPosition += lineHeight * 2;

      // Add underline
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition - 3, pageWidth - margin, yPosition - 3);
      yPosition += lineHeight;

      // Section content
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      
      // Split content into paragraphs and format properly
      const paragraphs = content.split('\n\n');
      
      paragraphs.forEach((paragraph, index) => {
        if (paragraph.trim()) {
          // Check for bullet points
          if (paragraph.includes('•')) {
            const bulletLines = paragraph.split('\n');
            bulletLines.forEach(line => {
              if (line.trim()) {
                if (yPosition > pageHeight - margin - 20) {
                  doc.addPage();
                  yPosition = margin;
                }
                
                const cleanLine = line.trim();
                const wrappedLines = doc.splitTextToSize(cleanLine, pageWidth - 2 * margin - 10);
                doc.text(wrappedLines, margin + (cleanLine.startsWith('•') ? 0 : 15), yPosition);
                yPosition += wrappedLines.length * lineHeight + 2;
              }
            });
          } else {
            // Regular paragraph
            if (yPosition > pageHeight - margin - 20) {
              doc.addPage();
              yPosition = margin;
            }
            
            const wrappedLines = doc.splitTextToSize(paragraph.trim(), pageWidth - 2 * margin);
            doc.text(wrappedLines, margin, yPosition);
            yPosition += wrappedLines.length * lineHeight + lineHeight;
          }
        }
      });

      // Add spacing between sections
      if (!isLast) {
        yPosition += lineHeight * 2;
      }
    };

    // Add all sections with proper formatting
    addSection('1. PARTIES INVOLVED', sowData.partiesInvolved);
    addSection('2. PROJECT SCOPE', sowData.projectScope);
    addSection('3. DELIVERABLES', sowData.deliverables);
    addSection('4. TIMELINE', sowData.timeline);
    addSection('5. PAYMENT TERMS', sowData.paymentTerms);
    addSection('6. SIGNATURES', sowData.signatureLines, true);

    // Add footer on each page
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      doc.text('Confidential Business Document', margin, pageHeight - 10);
    }

    doc.save('Beats-JordanRivers-SOW.pdf');
    
    toast({
      title: "Success",
      description: "Professional SOW downloaded as PDF",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          AI-Powered SOW Generator
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Upload a deal or agreement PDF to automatically generate a structured Statement of Work
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* API Key Input */}
        {showApiKeyInput && (
          <div className="p-4 border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">OpenAI API Key Required</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Enter your OpenAI API key to enable AI-powered SOW generation. You can get one at platform.openai.com
            </p>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="text-xs"
              />
              <Button 
                size="sm" 
                onClick={() => {
                  if (apiKey.trim()) {
                    setShowApiKeyInput(false);
                    toast({
                      title: "API Key Saved",
                      description: "You can now upload PDFs to generate SOWs",
                    });
                  }
                }}
                disabled={!apiKey.trim()}
              >
                Save & Continue
              </Button>
            </div>
          </div>
        )}

        {/* Show API Key prompt if not set */}
        {!apiKey && !showApiKeyInput && (
          <div className="p-4 border border-blue-200 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Ready to Generate SOWs</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Add your OpenAI API key to enable AI-powered SOW generation from your PDFs
            </p>
            <Button 
              size="sm" 
              onClick={() => setShowApiKeyInput(true)}
              className="gap-2"
            >
              <FileText className="h-4 w-4" />
              Add API Key
            </Button>
          </div>
        )}

        {/* File Upload */}
        <div className="space-y-4">
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
            <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <div className="space-y-2">
              <p className="text-sm font-medium">Upload PDF Document</p>
              <p className="text-xs text-muted-foreground">
                Select a deal or agreement PDF to generate an SOW
              </p>
              <Input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="max-w-xs mx-auto"
              />
            </div>
          </div>

          {file && (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4" />
              <span className="font-medium">{file.name}</span>
              <span className="text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {isProcessing && (
          <div className="space-y-2">
            <Progress value={progress} className="w-full" />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing document...
            </div>
          </div>
        )}

        {/* SOW Results */}
        {sowData && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold">Generated Statement of Work</h3>
              </div>
              <Button onClick={downloadSOWAsPDF} className="gap-2">
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>

            <div className="space-y-4 bg-muted/30 p-4 rounded-lg">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">1. Parties Involved</h4>
                <Textarea
                  value={sowData.partiesInvolved}
                  onChange={(e) => setSowData({...sowData, partiesInvolved: e.target.value})}
                  className="min-h-[60px]"
                />
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">2. Project Scope</h4>
                <Textarea
                  value={sowData.projectScope}
                  onChange={(e) => setSowData({...sowData, projectScope: e.target.value})}
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">3. Deliverables</h4>
                <Textarea
                  value={sowData.deliverables}
                  onChange={(e) => setSowData({...sowData, deliverables: e.target.value})}
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">4. Timeline</h4>
                <Textarea
                  value={sowData.timeline}
                  onChange={(e) => setSowData({...sowData, timeline: e.target.value})}
                  className="min-h-[60px]"
                />
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">5. Payment Terms</h4>
                <Textarea
                  value={sowData.paymentTerms}
                  onChange={(e) => setSowData({...sowData, paymentTerms: e.target.value})}
                  className="min-h-[60px]"
                />
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">6. Signature Lines</h4>
                <Textarea
                  value={sowData.signatureLines}
                  onChange={(e) => setSowData({...sowData, signatureLines: e.target.value})}
                  className="min-h-[60px]"
                />
              </div>
            </div>
          </div>
        )}

        {/* Loading Skeleton */}
        {isProcessing && progress > 50 && (
          <div className="space-y-4">
            <Skeleton className="h-4 w-48" />
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};